/**
 * Genre Icon System for The Country Club
 * 
 * Maps game genres to SVG icons and accent colors.
 * Since we have 170+ games across many genres, we use genre-based
 * visual identifiers instead of individual cover art.
 */

/**
 * Returns an emoji + color accent for a given genre string.
 * Covers all genres in the catalog with fallbacks.
 */
export function getGenreVisual(genre) {
  const g = (genre || '').toLowerCase();

  // Shooters
  if (g.includes('first person shooter') || g.includes('fps'))
    return { icon: '🔫', color: '#ef4444', bg: 'rgba(239,68,68,0.10)', border: 'rgba(239,68,68,0.20)' };
  if (g.includes('third person shooter'))
    return { icon: '🎯', color: '#f97316', bg: 'rgba(249,115,22,0.10)', border: 'rgba(249,115,22,0.20)' };
  if (g.includes('tactical'))
    return { icon: '🥷', color: '#a855f7', bg: 'rgba(168,85,247,0.10)', border: 'rgba(168,85,247,0.20)' };

  // Racing
  if (g.includes('racing') && g.includes('derby'))
    return { icon: '💥', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.20)' };
  if (g.includes('racing') && g.includes('motorcycle'))
    return { icon: '🏍️', color: '#06b6d4', bg: 'rgba(6,182,212,0.10)', border: 'rgba(6,182,212,0.20)' };
  if (g.includes('racing') && g.includes('rally'))
    return { icon: '🏁', color: '#22c55e', bg: 'rgba(34,197,94,0.10)', border: 'rgba(34,197,94,0.20)' };
  if (g.includes('racing') && g.includes('simulator'))
    return { icon: '🏎️', color: '#3b82f6', bg: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.20)' };
  if (g.includes('racing'))
    return { icon: '🚗', color: '#8b5cf6', bg: 'rgba(139,92,246,0.10)', border: 'rgba(139,92,246,0.20)' };

  // Combat
  if (g.includes('combat') && g.includes('vehicle'))
    return { icon: '🔥', color: '#ef4444', bg: 'rgba(239,68,68,0.10)', border: 'rgba(239,68,68,0.20)' };
  if (g.includes('combat') && g.includes('flight'))
    return { icon: '✈️', color: '#0ea5e9', bg: 'rgba(14,165,233,0.10)', border: 'rgba(14,165,233,0.20)' };

  // Sports
  if (g.includes('football') && !g.includes('australian'))
    return { icon: '🏈', color: '#854d0e', bg: 'rgba(133,77,14,0.10)', border: 'rgba(133,77,14,0.20)' };
  if (g.includes('australian'))
    return { icon: '🏉', color: '#65a30d', bg: 'rgba(101,163,13,0.10)', border: 'rgba(101,163,13,0.20)' };
  if (g.includes('basketball') || g.includes('street basketball'))
    return { icon: '🏀', color: '#ea580c', bg: 'rgba(234,88,12,0.10)', border: 'rgba(234,88,12,0.20)' };
  if (g.includes('baseball'))
    return { icon: '⚾', color: '#dc2626', bg: 'rgba(220,38,38,0.10)', border: 'rgba(220,38,38,0.20)' };
  if (g.includes('soccer'))
    return { icon: '⚽', color: '#16a34a', bg: 'rgba(22,163,74,0.10)', border: 'rgba(22,163,74,0.20)' };
  if (g.includes('hockey'))
    return { icon: '🏒', color: '#0284c7', bg: 'rgba(2,132,199,0.10)', border: 'rgba(2,132,199,0.20)' };
  if (g.includes('golf'))
    return { icon: '⛳', color: '#22c55e', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.25)' };
  if (g.includes('tennis'))
    return { icon: '🎾', color: '#84cc16', bg: 'rgba(132,204,22,0.10)', border: 'rgba(132,204,22,0.20)' };
  if (g.includes('snowboard'))
    return { icon: '🏂', color: '#38bdf8', bg: 'rgba(56,189,248,0.10)', border: 'rgba(56,189,248,0.20)' };
  if (g.includes('skate'))
    return { icon: '🛹', color: '#a78bfa', bg: 'rgba(167,139,250,0.10)', border: 'rgba(167,139,250,0.20)' };
  if (g.includes('billiards') || g.includes('pool') || g.includes('snooker'))
    return { icon: '🎱', color: '#6b7280', bg: 'rgba(107,114,128,0.10)', border: 'rgba(107,114,128,0.20)' };

  // Fighting
  if (g.includes('fighting'))
    return { icon: '🥊', color: '#e11d48', bg: 'rgba(225,29,72,0.10)', border: 'rgba(225,29,72,0.20)' };

  // RPG
  if (g.includes('role playing') || g.includes('rpg'))
    return { icon: '⚔️', color: '#d946ef', bg: 'rgba(217,70,239,0.10)', border: 'rgba(217,70,239,0.20)' };
  if (g.includes('mmorpg'))
    return { icon: '🌍', color: '#8b5cf6', bg: 'rgba(139,92,246,0.12)', border: 'rgba(139,92,246,0.25)' };

  // Strategy
  if (g.includes('strategy'))
    return { icon: '🗺️', color: '#0d9488', bg: 'rgba(13,148,136,0.10)', border: 'rgba(13,148,136,0.20)' };

  // Action / Adventure
  if (g.includes('action') || g.includes('adventure') || g.includes('survival'))
    return { icon: '🧟', color: '#b91c1c', bg: 'rgba(185,28,28,0.10)', border: 'rgba(185,28,28,0.20)' };

  // Music
  if (g.includes('music'))
    return { icon: '🎵', color: '#ec4899', bg: 'rgba(236,72,153,0.10)', border: 'rgba(236,72,153,0.20)' };

  // Misc
  if (g.includes('party'))
    return { icon: '🎉', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.20)' };
  if (g.includes('poker'))
    return { icon: '🃏', color: '#dc2626', bg: 'rgba(220,38,38,0.10)', border: 'rgba(220,38,38,0.20)' };
  if (g.includes('trivia') || g.includes('board'))
    return { icon: '🎲', color: '#7c3aed', bg: 'rgba(124,58,237,0.10)', border: 'rgba(124,58,237,0.20)' };
  if (g.includes('hunting'))
    return { icon: '🦌', color: '#78716c', bg: 'rgba(120,113,108,0.10)', border: 'rgba(120,113,108,0.20)' };
  if (g.includes('paintball'))
    return { icon: '🎨', color: '#f97316', bg: 'rgba(249,115,22,0.10)', border: 'rgba(249,115,22,0.20)' };

  // Default
  return { icon: '🎮', color: '#6b7280', bg: 'rgba(107,114,128,0.08)', border: 'rgba(107,114,128,0.15)' };
}
